import type { DocumentData } from '../../.types/tea.ts';
import {
  type CreateDocumentBody,
  type MetaProperty,
  createDocument as createDocumentRequest,
} from './createDocument.ts';
import { getDocument as getDocumentRequest } from './getDocument.ts';

/**
 * Class that handles document storage via API operations. Allows all methods to inherit shared data such as `documentUuid`, `meta`, `baseUrl` etc
 */
export class TeaStorageClient {
  constructor(
    private readonly accessToken: string,
    private readonly baseUrl: string,
    private readonly documentGroupId: string | undefined,
    private readonly documentUuid: string,
    private readonly referrer: string,
    private readonly meta: MetaProperty = {},
  ) {
    this.accessToken = accessToken;
    this.baseUrl = baseUrl;
    this.documentGroupId = documentGroupId;
    this.documentUuid = documentUuid;
    this.referrer = referrer;
    this.meta = meta;
  }

  /**
   * Loads an existing document from the API
   */
  async loadDocument(): Promise<DocumentData> {
    const { document, error } = await getDocumentRequest(
      this.baseUrl,
      this.accessToken,
      this.documentUuid,
      this.referrer,
    );

    if (error) {
      throw error;
    }

    if (!document?.data) {
      throw new Error('Invalid document data');
    }

    const documentState: DocumentData = {
      document: JSON.parse(document.data.attributes.document),
      html: document.data.attributes.html,
      // @ts-expect-error the `text` attribute is missing from the auto-generated GetDocumentResponse type
      // however, it is present in the API response
      text: document.data.attributes?.text ?? '',
    };
    return documentState;
  }

  /**
   * Saves a document to the API
   */
  async saveDocument(body: CreateDocumentBody): Promise<void> {
    const { error } = await createDocumentRequest(
      this.baseUrl,
      this.accessToken,
      this.documentUuid,
      this.referrer,
      body,
    );

    if (error) throw error;
  }

  /**
   * Creates a new document in the API
   */
  async createDocument(): Promise<DocumentData> {
    const emptyTipTapDocument = {
      type: 'doc',
      content: [
        {
          type: 'paragraph',
        },
      ],
    };

    await this.saveDocument({
      data: {
        id: this.documentUuid,
        type: 'document',
        attributes: {
          document: JSON.stringify(emptyTipTapDocument),
          html: '<p></p>',
          text: '',
          meta: this.meta,
          documentGroupId: this.documentGroupId,
        },
      },
    });

    const documentState: DocumentData = {
      document: emptyTipTapDocument,
      html: '<p></p>',
      text: '',
    };
    return documentState;
  }
}
